import { Cookie, useCookie } from 'next-cookie'
import { CookieSetOptions } from 'universal-cookie'
import { CookieContext } from './sessionService'

const propertyName = 'utmSource'
const queryParameter = 'utm_source'

const maxLength = 32
const validityMs = 24 * 60 * 60 * 1000

const options: CookieSetOptions = {
  maxAge: 24 * 60 * 60, // 24 hours
  path: '/',
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax'
}

type UtmSourceState = {
  source?: string
  sourceAt?: number
  codShownAt?: number
}

const isFresh = (timestamp?: number): boolean =>
  typeof timestamp === 'number' && Date.now() - timestamp < validityMs

const readState = (cookie: Cookie): UtmSourceState => {
  const value = cookie.get<UtmSourceState | string | number>(propertyName)
  if (typeof value === 'string' || typeof value === 'number') {
    const source = String(value).trim()
    return source ? { source } : {}
  }
  return value && typeof value === 'object' ? value : {}
}

const hasValidSource = (state: UtmSourceState): boolean =>
  !!state.source && (state.sourceAt === undefined || isFresh(state.sourceAt))

const writeState = (cookie: Cookie, state: UtmSourceState): void => {
  const now = Date.now()
  const nextState: UtmSourceState = {}
  if (hasValidSource(state)) {
    nextState.source = state.source
    nextState.sourceAt = state.sourceAt ?? now
  }
  if (isFresh(state.codShownAt)) {
    nextState.codShownAt = state.codShownAt
  }
  cookie.set(propertyName, nextState, options)
}

const readFromPath = (path: string): string | undefined => {
  try {
    const value = new URL(`http://example.com${path}`).searchParams.get(
      queryParameter
    )
    const trimmed = value?.trim() ?? ''
    return trimmed ? trimmed.slice(0, maxLength) : undefined
  } catch {
    return undefined
  }
}

export const captureUtmSource = (
  path: string,
  context?: CookieContext
): void => {
  const source = readFromPath(path)
  if (!source) {
    return
  }
  const cookie = useCookie(context)
  const state = readState(cookie)
  if (state.source === source && hasValidSource(state)) {
    return
  }
  writeState(cookie, { ...state, source, sourceAt: Date.now() })
}

export const markCodShown = (context?: CookieContext): void => {
  const cookie = useCookie(context)
  writeState(cookie, { ...readState(cookie), codShownAt: Date.now() })
}

export const getUtmSource = (context?: CookieContext): string | undefined => {
  const state = readState(useCookie(context))
  if (isFresh(state.codShownAt)) {
    return undefined
  }
  return hasValidSource(state) ? state.source : undefined
}
